<script setup lang="ts">
import type { UseCustomerBiometryData } from '~/store-v2/contract-formalization/useCustomerBiometry'
import type { Tags } from '~/utils/customer-register/Biometry'
import { useToast } from '@solfacil/girassol'
import IconCheck from '~icons/material-symbols/check-circle-outline-rounded'
import IconSchedule from '~icons/material-symbols/schedule-outline-rounded'
import PendingUpdateEquipmentAlert from '~/components/customer/alerts/PendingUpdateEquipmentAlert.vue'
import { usePendingUpdateEquipment } from '~/composables/equipment/usePendingUpdateEquipment'
import useBiometryResend from '~/store-v2/contract-formalization/useBiometryResend'
import useContractResend from '~/store-v2/contract-formalization/useContractResend'
import useFinancing from '~/store-v2/financings/useFinancing'
import {
  buildTags,
  getBiometryResendTimerKey,
  getContractResendTimerKey,
  RESEND_COOLDOWN_SECONDS,
  STEP_CONFIGURATION,
  STEP_KEYS,
} from './biometryTimeline.constants'
import TimelineStatusDates from './TimelineStatusDates.vue'

const props = defineProps<{
  data: NonNullable<UseCustomerBiometryData>
  disabled: boolean
  showDateProps?: boolean | null
  biometryStageStatus?: string
  // Terminal financing read-only mode: blocks the pending TAF actions (resend/delete)
  readOnly?: boolean
}>()

const { t } = useI18n()
const route = useRoute<any>()
const toast = useToast()
const projectId = computed(() => String(route.params.id))
const { hasPendingTAF } = usePendingUpdateEquipment(projectId)
const tag: Tags = buildTags(t) as unknown as Tags

const hasBiometric = computed(() => props.data?.contract?.has_biometric)

const step = computed(() => {
  const createStep = (stepKey: keyof typeof STEP_CONFIGURATION, key: number) => ({
    key,
    id: STEP_CONFIGURATION[stepKey].id,
    text: t(STEP_CONFIGURATION[stepKey].textKey),
    status: tag[STEP_CONFIGURATION[stepKey].status as keyof Tags],
  })

  return {
    biometry: createStep('biometry', STEP_KEYS.BIOMETRY),
    contract: createStep('contract', STEP_KEYS.CONTRACT),
    approval: createStep('approval', STEP_KEYS.APPROVAL),
    reproved: createStep('reproved', STEP_KEYS.REPROVED),
  }
})

const {
  data: financing,
} = useFinancing(projectId)

const {
  mutateAsync: mutateContractResend,
  isPending: isPendingContractResend,
} = useContractResend(projectId)

const {
  mutateAsync: mutateBiometryResend,
  isPending: isPendingBiometryResend,
} = useBiometryResend(projectId)

const biometryStatus = computed(() => {
  if (props.data.biometry.status === 'completed') {
    return tag.accomplished
  }

  return (props.data.biometry.status === 'waiting' ? tag.waiting_biometry : tag[props.data.biometry.status as keyof Tags]) || tag.waiting_biometry
})

const contractStatus = computed(() => {
  if (props.data.contract.status === 'completed') {
    return tag.accomplished
  }
  if (hasPendingTAF.value) {
    return tag.waiting
  }
  return tag[props.data.contract.status as keyof Tags] || tag.waiting
})

const approvalStatus = computed(() => {
  return tag[props.data.approval as keyof Tags] || tag.waiting
})

const timelineDates = computed(() => {
  const metadata
    = financing.value?.section_statuses?.biometry_and_contract.metadata || {}

  if (!metadata) {
    return {
      lastSubmittedAtBiometry: null,
      completedAtBiometry: null,
      lastSubmittedAtContract: null,
      completedAtContract: null,
    }
  }

  return {
    lastSubmittedAtBiometry: metadata?.biometry?.last_submitted_at,
    completedAtBiometry: metadata?.biometry?.completed_at,
    lastSubmittedAtContract: metadata?.contract?.last_submitted_at,
    completedAtContract: metadata?.contract?.completed_at,
  }
})

const isBiometryResendCooldownActive = ref(false)
const biometryResendCooldownRemaining = ref(0)
let biometryResendCooldownInterval: NodeJS.Timeout | null = null

const isContractResendCooldownActive = ref(false)
const contractResendCooldownRemaining = ref(0)
let contractResendCooldownInterval: NodeJS.Timeout | null = null

const now = () => Math.floor(Date.now() / 1000)

function startBiometryResendCooldown() {
  const expiry = now() + RESEND_COOLDOWN_SECONDS
  localStorage.setItem(getBiometryResendTimerKey(projectId.value), expiry.toString())
  updateBiometryResendCooldown()
  biometryResendCooldownInterval = setInterval(updateBiometryResendCooldown, 1000)
}

function startContractResendCooldown() {
  const expiry = now() + RESEND_COOLDOWN_SECONDS
  localStorage.setItem(getContractResendTimerKey(projectId.value), expiry.toString())
  updateContractResendCooldown()
  contractResendCooldownInterval = setInterval(updateContractResendCooldown, 1000)
}

function updateBiometryResendCooldown() {
  const expiry = Number(localStorage.getItem(getBiometryResendTimerKey(projectId.value)) || 0)
  const remaining = expiry - now()

  if (remaining > 0) {
    biometryResendCooldownRemaining.value = remaining
    isBiometryResendCooldownActive.value = true
  }
  else {
    biometryResendCooldownRemaining.value = 0
    isBiometryResendCooldownActive.value = false
    if (biometryResendCooldownInterval)
      clearInterval(biometryResendCooldownInterval)
  }
}

function updateContractResendCooldown() {
  const expiry = Number(localStorage.getItem(getContractResendTimerKey(projectId.value)) || 0)
  const remaining = expiry - now()

  if (remaining > 0) {
    contractResendCooldownRemaining.value = remaining
    isContractResendCooldownActive.value = true
  }
  else {
    contractResendCooldownRemaining.value = 0
    isContractResendCooldownActive.value = false
    if (contractResendCooldownInterval)
      clearInterval(contractResendCooldownInterval)
  }
}

async function resendBiometry() {
  if (props.disabled || isBiometryResendCooldownActive.value || isPendingBiometryResend.value)
    return

  try {
    startBiometryResendCooldown()
    await mutateBiometryResend()
    toast.createSuccessToast({
      title: t('customer.biometry.resend.success.title'),
      description: t('customer.biometry.resend.success.biometry_description'),
    })
  }
  catch (error) {
    console.error(error)
    toast.createErrorToast({
      title: t('customer.biometry.resend.error.title'),
      description: t('customer.biometry.resend.error.biometry_description'),
    })
  }
}

async function resendContract() {
  if (props.disabled || isContractResendCooldownActive.value || isPendingContractResend.value)
    return

  try {
    startContractResendCooldown()
    await mutateContractResend()
    toast.createSuccessToast({
      title: t('customer.biometry.resend.success.title'),
      description: t('customer.biometry.resend.success.contract_description'),
    })
  }
  catch (error) {
    console.error(error)
    toast.createErrorToast({
      title: t('customer.biometry.resend.error.title'),
      description: t('customer.biometry.resend.error.contract_description'),
    })
  }
}

const showResendBiometryButton = computed(() => {
  const isWaitingCustomer = props.biometryStageStatus === 'waiting_customer'
  const isBiometryInProgress = props.data?.biometry?.status === 'in_progress'

  return isWaitingCustomer && isBiometryInProgress
})

const showResendContractButton = computed(() => {
  const isWaitingCustomer = props.biometryStageStatus === 'waiting_customer'
  const isContractInProgress = props.data?.contract?.status === 'in_progress'
  const isContractWaitingAndBiometryCompleted = props.data?.contract?.status === 'waiting' && props.data?.biometry?.status === 'completed'

  return isWaitingCustomer && (isContractInProgress || isContractWaitingAndBiometryCompleted)
})

onMounted(() => {
  updateBiometryResendCooldown()
  updateContractResendCooldown()

  if (biometryResendCooldownRemaining.value > 0) {
    biometryResendCooldownInterval = setInterval(updateBiometryResendCooldown, 1000)
  }

  if (contractResendCooldownRemaining.value > 0) {
    contractResendCooldownInterval = setInterval(updateContractResendCooldown, 1000)
  }
})
</script>

<template>
  <ol v-if="props.data" class="steps-core -column">
    <li class="step">
      <div class="element">
        <div class="line" />

        <div class="link">
          <div class="circle shadow-weak">
            <span>{{ step.biometry.key }}</span>
          </div>
        </div>
      </div>

      <div class="link mb-4">
        <div class="label">
          <div class="flex items-center">
            <div class="title fonts-body-large-bold text-neutral-low-dark -mt-1" data-testid="biometry-step-title">
              {{ t('customer.biometry.steps.biometry') }}
            </div>

            <SolTag
              :id="biometryStatus.id"
              :text="biometryStatus.text"
              :variant="biometryStatus.variant"
              :size="biometryStatus.size"
              :type="biometryStatus.type"
              data-testid="biometry-status-tag"
              class="ml-2"
            />
          </div>

          <div class="subtitle">
            <ul class="list-disc ml-5 my-2 fonts-body-small-regular text-neutral-low-medium">
              <li>{{ t(hasBiometric ? 'customer.biometry.description.has_biometric' : 'customer.biometry.description.biometry') }}</li>
            </ul>
          </div>

          <div v-for="(item, index) in data.biometry.signers" :key="index">
            <div class="flex items-center my-1">
              <div class="mr-2">
                {{ item.name }}
              </div>
              <IconCheck v-if="item.checked && data.biometry.status !== 'reproved'" class="text-brand-secondary-pure" />
              <IconSchedule v-else-if="data.biometry.status !== 'reproved'" />
            </div>
          </div>

          <TimelineStatusDates
            v-if="showDateProps"
            :last-submitted-at="timelineDates.lastSubmittedAtBiometry"
            :completed-at="timelineDates.completedAtBiometry"
            data-testid="biometry-dates"
          />
          <div
            v-if="!hasBiometric"
            class="flex w-full items-end justify-start pt-4 md:system:mt-0 gap-2"
          >
            <SolButton
              v-if="showResendBiometryButton"
              id="btn-resend-biometry"
              size="medium"
              class="w-full md:system:w-auto"
              variant="secondary"
              :disabled="disabled || isBiometryResendCooldownActive || isPendingBiometryResend"
              @click="resendBiometry()"
            >
              <template #default>
                {{
                  isBiometryResendCooldownActive
                    ? t('customer.biometry.resend.wait', { seconds: biometryResendCooldownRemaining })
                    : t('customer.biometry.buttons.resend_biometry') }}
              </template>
            </SolButton>
          </div>
        </div>
      </div>
    </li>

    <li class="step">
      <div class="element">
        <div class="line" />

        <div class="link">
          <div class="circle shadow-weak">
            <span>{{ step.contract.key }}</span>
          </div>
        </div>
      </div>

      <div class="contract link mb-4">
        <div class="label">
          <div class="flex items-center">
            <div class="title fonts-body-large-bold text-neutral-low-dark -mt-1" data-testid="contract-step-title">
              {{ step.contract.text }}
            </div>

            <SolTag
              :id="contractStatus.id"
              :text="contractStatus.text"
              :variant="contractStatus.variant"
              :size="contractStatus.size"
              :type="contractStatus.type"
              data-testid="contract-status-tag"
              class="ml-2"
            />
          </div>

          <div class="subtitle">
            <ul class="list-disc ml-5 my-2 fonts-body-small-regular text-neutral-low-medium">
              <li>{{ t('customer.biometry.description.contract') }}</li>
            </ul>
          </div>

          <PendingUpdateEquipmentAlert
            :title="t('customer.equipment_update.alert.pending_biometry_timeline_title')"
            :description="t('customer.equipment_update.alert.pending_biometry_timeline_description')"
            :read-only="readOnly"
          />

          <template v-if="!hasPendingTAF">
            <div v-for="(item, index) in data.contract.signers" :key="index">
              <div class="flex items-center my-1">
                <div class="mr-2">
                  {{ item.name }}
                </div>
                <IconCheck v-if="item.checked" class="text-brand-secondary-pure" />
                <IconSchedule v-else />
              </div>
            </div>
          </template>

          <TimelineStatusDates
            v-if="showDateProps && !hasPendingTAF"
            :last-submitted-at="timelineDates.lastSubmittedAtContract"
            :completed-at="timelineDates.completedAtContract"
            data-testid="contract-dates"
          />

          <div v-if="showResendContractButton" class="flex w-full items-end justify-start pt-4 md:system:mt-0 gap-2">
            <SolButton
              id="btn-resend-contract"
              size="medium"
              class="w-full md:system:w-auto"
              variant="secondary"
              :disabled="disabled || isContractResendCooldownActive || isPendingContractResend"
              @click="resendContract()"
            >
              <template #default>
                {{ isContractResendCooldownActive ? t('customer.biometry.resend.wait', { seconds: contractResendCooldownRemaining }) : t('customer.biometry.buttons.resend_contract') }}
              </template>
            </SolButton>
          </div>
        </div>
      </div>
    </li>

    <li class="step">
      <div class="element">
        <div class="line" />

        <div class="link">
          <div class="circle shadow-weak">
            <span>{{ step.approval.key }}</span>
          </div>
        </div>
      </div>

      <div class="link mb-4">
        <div class="label">
          <div class="flex items-center">
            <div class="title fonts-body-large-bold text-neutral-low-dark -mt-1" data-testid="approval-step-title">
              {{ step.approval.text }}
            </div>

            <SolTag
              :id="approvalStatus.id"
              :text="approvalStatus.text"
              :variant="approvalStatus.variant"
              :size="approvalStatus.size"
              :type="approvalStatus.type"
              data-testid="approval-status-tag"
              class="ml-2"
            />
          </div>

          <div class="subtitle">
            <ul class="list-disc ml-5 my-2 fonts-body-small-regular text-neutral-low-medium">
              <li>{{ t('customer.biometry.description.approval') }}</li>
            </ul>
          </div>
        </div>
      </div>
    </li>
  </ol>
</template>

<style lang="scss" scoped>
.steps-core {
  @apply flex justify-between flex-shrink-0;

  > .step {
    @apply relative;
    @apply w-full;

    &:last-of-type > .element > .line {
      @apply hidden;
    }
  }

  > .step > .element {
    @apply flex justify-center relative;

    > .link {
      @apply w-fit-content h-fit-content pointer-events-none;
    }

    > .link > .circle {
      @apply flex justify-center items-center;
      @apply w-xs h-xs rounded-half;
      @apply overflow-hidden;
      @apply pointer-events-none;
    }

    > .line {
      @apply absolute;
      @apply top-4xs;
      @apply bg-neutral-high-dark;

      height: 1px;
      left: calc(50% + 16px);
      width: calc(100% - 32px);
    }
  }

  &.-column {
    @apply flex-col h-full;

    > .step {
      @apply flex flex-grow;
    }

    > .step > .element > .line {
      @apply top-xs;

      left: 50%;
      width: 1px;
      height: calc(100% - 32px);
    }

    > .step > .link > .label {
      @apply mt-nano ml-nano;
      @apply text-left;
    }
  }

  @media screen and (max-width: 768px) {
    .contract {
      &:deep(.label) {
        max-width: 90%;

        .sol-alert-core {
          @apply p-2;
        }

        svg {
          @apply mr-2 w-4;
        }

        #button-delete-update-equipment {
          @apply justify-end
        }
      }
    }
  }
}
</style>
